import { useMemo, useState } from "react";

import { SearchOutlined } from "@ant-design/icons";
import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Divider, Drawer } from "@impero-com/ui";
import { Input } from "antd";

import { WebRoute } from "routes";

import * as theme from "./EntitiesDrawer.css";
import { EntitiesCellEntity } from "./types";

interface EntitiesDrawerProps {
    entities: Array<EntitiesCellEntity>;
    open: boolean;
    onClose: () => void;
}

export function EntitiesDrawer({
    entities,
    open,
    onClose,
}: EntitiesDrawerProps) {
    const { t } = useTranslation();
    const [search, setSearch] = useState("");

    const handleClose = () => {
        setSearch("");
        onClose();
    };

    const matches = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return entities;
        }
        return entities.filter((entity) =>
            entity.name.toLowerCase().includes(query)
        );
    }, [entities, search]);

    return (
        <Drawer
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    handleClose();
                }
            }}
            variant="default"
            closeAriaLabel={t("ControlPrograms_EntitiesDrawer_Close")}
            withHeader={{
                title: t("ControlPrograms_EntitiesDrawer_Title"),
            }}
            withFooter={
                <Button variant="primary" onClick={handleClose}>
                    {t("ControlPrograms_EntitiesDrawer_Close")}
                </Button>
            }
        >
            <Input
                className={theme.drawerSearch}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t(
                    "ControlPrograms_EntitiesDrawer_SearchPlaceholder"
                )}
                prefix={<SearchOutlined />}
                suffix={
                    <span className={theme.searchCount}>
                        {matches.length}/{entities.length}
                    </span>
                }
                allowClear
                autoFocus
            />
            {matches.length === 0 ? (
                <div className={theme.drawerEmpty}>
                    {t("ControlPrograms_EntitiesDrawer_NoMatches")}
                </div>
            ) : (
                <ul className={theme.drawerList}>
                    {matches.map((entity) => (
                        <li key={entity.id}>
                            <ProtectedLink
                                href={`${WebRoute.ENTITIES}/${entity.id}`}
                                hasAccess={true}
                            >
                                {entity.name}
                            </ProtectedLink>
                            <Divider spacing="small" />
                        </li>
                    ))}
                </ul>
            )}
        </Drawer>
    );
}
